import { ChatContact } from '../types/chat.types';
import { formatChatListTime } from '../../shared-lib/functions/moment-common-function';
import { parseBlockFlags } from './chat-block-utils';

/** `GetUserChatSummary` `data` payload (pinned + unpinned arrays). */
export interface ChatSummaryDataPayload {
  pinnedChats?: unknown[];
  unPinnedChats?: unknown[];
}

export function isChatSummarySplitPayload(data: unknown): data is ChatSummaryDataPayload {
  return (
    data !== null &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    ('pinnedChats' in data || 'unPinnedChats' in data)
  );
}

/**
 * Merges pinned and unpinned chat arrays from GetUserChatSummary.
 * Pinned items are forced `isPinned: true`; duplicates (by id) are skipped (pinned wins).
 */
export function mergeChatSummaryRawItems(data: unknown): Record<string, unknown>[] {
  if (!data) {
    return [];
  }
  if (Array.isArray(data)) {
    return data as Record<string, unknown>[];
  }
  if (!isChatSummarySplitPayload(data)) {
    return [];
  }

  const pinned = Array.isArray(data.pinnedChats) ? data.pinnedChats : [];
  const unpinned = Array.isArray(data.unPinnedChats) ? data.unPinnedChats : [];
  const seen = new Set<string>();
  const merged: Record<string, unknown>[] = [];

  const addItem = (item: unknown, forcePinned: boolean) => {
    if (!item || typeof item !== 'object') {
      return;
    }
    const row = item as Record<string, unknown>;
    const id = row.id != null ? String(row.id) : '';
    if (!id || seen.has(id)) {
      return;
    }
    seen.add(id);
    merged.push({
      ...row,
      isPinned: forcePinned || Boolean(row.isPinned),
    });
  };

  pinned.forEach((item) => addItem(item, true));
  unpinned.forEach((item) => addItem(item, false));

  return merged;
}

/** Flat merged list from API `response.data.data`. */
export function getMergedChatSummaryItems(apiData: unknown): Record<string, unknown>[] {
  return mergeChatSummaryRawItems(apiData);
}

export function sortChatContactsByPinnedAndActivity(contacts: ChatContact[]): ChatContact[] {
  return [...contacts].sort((a, b) => {
    if (a.isPinned !== b.isPinned) {
      return a.isPinned ? -1 : 1;
    }
    const dateA = a.lastMsgDate ? new Date(a.lastMsgDate).getTime() : 0;
    const dateB = b.lastMsgDate ? new Date(b.lastMsgDate).getTime() : 0;
    return dateB - dateA;
  });
}

export function mapApiItemToChatContact(
  item: Record<string, unknown>,
  userTimezone: string,
  weekdayFormat: 'long' | 'short'
): ChatContact {
  const lastMsgDate = String(item.lastMsgDate ?? '');
  const firstName = String(item.firstName ?? '');
  const lastName = String(item.lastName ?? '');
  const userAvatar = String(item.userAvatar ?? '/api/placeholder/40/40');
  const unReadCount = Number(item.unReadCount ?? 0);
  const blockFlags = parseBlockFlags(item);

  return {
    id: String(item.id ?? ''),
    email: String(item.email ?? ''),
    groupName: String(item.groupName ?? ''),
    firstName,
    lastName,
    userAvatar,
    lastMessage: String(item.lastMessage ?? ''),
    lastMessageSender: String(item.lastMessageSender ?? ''),
    lastMsgDate,
    unReadCount,
    isAGroup: Boolean(item.isAGroup),
    isPinned: Boolean(item.isPinned),
    isMarkFavourite: Boolean(item.isMarkFavourite),
    lastSeen: String(item.lastSeen ?? ''),
    isOnline: Boolean(item.isOnline),
    deliveredDate: (item.deliveredDate as string | null) ?? null,
    isRead: Boolean(item.isRead),
    isBlockedByMe: blockFlags.isBlockedByMe,
    hasBlockedMe: blockFlags.hasBlockedMe,
    canSendMessage: blockFlags.canSendMessage,
    name: `${firstName} ${lastName}`.trim(),
    title: String(item.groupName ?? ''),
    avatar: userAvatar,
    lastMessageTime: formatChatListTime(lastMsgDate, userTimezone, undefined, weekdayFormat),
    unreadCount: unReadCount,
  };
}

export function mapAndSortChatSummaryApiData(
  apiData: unknown,
  userTimezone: string,
  weekdayFormat: 'long' | 'short'
): ChatContact[] {
  const rawItems = mergeChatSummaryRawItems(apiData);
  const mapped = rawItems.map((item) => mapApiItemToChatContact(item, userTimezone, weekdayFormat));
  return sortChatContactsByPinnedAndActivity(mapped);
}

